import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from '@/hooks/useLocaleStorage'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { TheatricalPromotion } from '@/views/TicketCheckoutViews/ShowtimesView/components/TheatricalPromotionContext'
import { useTicketsContext } from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketsContext'
import { isAngelIntegratedCountry } from '@/views/TicketCheckoutViews/ShowtimesView/utils/utils'

export const useTheatricalModalController = (
  theatricalPromotion: TheatricalPromotion | undefined,
  theatricalSlug: string,
  isReady: boolean,
): [boolean, () => void] => {
  const { query, asPath, isReady: isRouterReady } = useSafeRouter()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isFreeTicketPromo, isEditReservationFlow, countryCode } = useTicketsContext()
  const { promoType, hasModal, discount } = theatricalPromotion ?? {}

  const storageKey = useMemo(() => {
    return discount
      ? `discount-modal-opened-${promoType}-${discount}-${theatricalSlug}`
      : `discount-modal-opened-${promoType}-${theatricalSlug}`
  }, [discount, promoType, theatricalSlug])

  const {
    storedValue: hasSeenModal,
    setStoredValue: setHasSeenModal,
    isReady: isLocaleStorageReady,
  } = useLocalStorage<boolean>(storageKey ?? '', false, 'boolean', !promoType)

  const isAngelIntegrated = isAngelIntegratedCountry(countryCode)
  const shouldHideDiscountsForMarketing = query?.promoOverride === 'true'
  const isSceneModalPlayerPage = asPath?.toLowerCase().includes('scene') && !asPath?.toLowerCase().includes('static')
  const isGuildShareFlow = query?.flow === 'guild-share'

  const canShowModal = useMemo(() => {
    return (
      isReady &&
      storageKey &&
      hasModal &&
      !!promoType &&
      isRouterReady &&
      isLocaleStorageReady &&
      !hasSeenModal &&
      !isEditReservationFlow &&
      isAngelIntegrated &&
      !shouldHideDiscountsForMarketing &&
      !isSceneModalPlayerPage &&
      !query?.highlightGroupTickets &&
      !query?.groupDiscountCode &&
      !isFreeTicketPromo &&
      !isGuildShareFlow
    )
  }, [
    hasModal,
    hasSeenModal,
    isAngelIntegrated,
    isEditReservationFlow,
    isFreeTicketPromo,
    isGuildShareFlow,
    isLocaleStorageReady,
    isReady,
    isRouterReady,
    isSceneModalPlayerPage,
    promoType,
    query?.highlightGroupTickets,
    query?.groupDiscountCode,
    shouldHideDiscountsForMarketing,
    storageKey,
  ])

  const closeModal = useCallback(() => setIsModalOpen(false), [setIsModalOpen])

  useEffect(() => {
    if (canShowModal) {
      setIsModalOpen(true)
      setHasSeenModal(true)
    }
  }, [canShowModal, setHasSeenModal])

  return useMemo(() => {
    return [isModalOpen, closeModal]
  }, [closeModal, isModalOpen])
}
